import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Filter, Search, ArrowUpDown, Leaf, Package } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { products } from '../mock';

export const Products = () => {
  const [capacityFilter, setCapacityFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState('default');
  const { t } = useTranslation();

  // Get translated product data
  const getProductData = (product) => {
    const itemData = t(`products.items.${product.id}`, { returnObjects: true });
    return {
      ...product,
      name: itemData?.name || product.id,
      shortDesc: itemData?.shortDesc || '',
      material: itemData?.material || '',
      loadCapacity: itemData?.loadCapacity || ''
    };
  };

  const translatedProducts = products.map(getProductData);

  const filteredAndSortedProducts = useMemo(() => {
    let result = translatedProducts.filter((product) => {
      // Search filter
      const matchesSearch = searchQuery === '' || 
        product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.shortDesc.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category.toLowerCase().includes(searchQuery.toLowerCase());
      
      // Capacity filter
      const matchesCapacity = capacityFilter === 'all' || product.capacity === capacityFilter;
      
      // Type filter
      const matchesType = typeFilter === 'all' || product.type === typeFilter;

      // Category filter
      const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
      
      return matchesSearch && matchesCapacity && matchesType && matchesCategory;
    });

    // Sort by size
    if (sortOrder === 'size-asc') {
      result = [...result].sort((a, b) => a.capacityValue - b.capacityValue);
    } else if (sortOrder === 'size-desc') {
      result = [...result].sort((a, b) => b.capacityValue - a.capacityValue);
    }

    return result;
  }, [translatedProducts, searchQuery, capacityFilter, typeFilter, categoryFilter, sortOrder]);

  const getTypeTranslation = (type) => {
    const typeMap = {
      'residential': t('products.residential'),
      'municipal': t('products.municipal'),
      'industrial': t('products.industrial')
    };
    return typeMap[type] || type;
  };

  const getCategoryTranslation = (category) => {
    return t(`products.categories.${category}`, { defaultValue: category });
  };

  // Get unique capacities for filter
  const uniqueCapacities = [...new Set(products.map(p => p.capacity))].sort((a, b) => {
    const numA = parseInt(a.replace(/[^0-9]/g, ''));
    const numB = parseInt(b.replace(/[^0-9]/g, ''));
    return numA - numB;
  });

  return (
    <div className="flex flex-col min-h-screen">
      {/* Header Section */}
      <section className="bg-gradient-to-br from-neutral-900 via-neutral-800 to-emerald-900 text-white py-12 sm:py-16 lg:py-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-emerald-400 font-semibold text-xs sm:text-sm uppercase tracking-wider mb-2 sm:mb-4">{t('products.subtitle')}</span>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4 lg:mb-6">{t('products.title')}</h1>
          <p className="text-base sm:text-lg lg:text-xl text-neutral-300 max-w-3xl leading-relaxed">
            {t('products.description')}
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-neutral-200/50 py-4 sm:py-6 lg:py-8 sticky top-16 sm:top-20 z-40 backdrop-blur-sm bg-white/95">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {/* Search Bar */}
          <div className="mb-4 sm:mb-6">
            <div className="relative w-full lg:max-w-xl">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={18} />
              <Input
                type="text"
                placeholder={t('products.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10 sm:h-12 text-sm sm:text-base"
              />
            </div>
          </div>
          
          {/* Filters */}
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center text-neutral-700 font-semibold text-sm sm:text-base lg:text-lg">
                <Filter size={18} className="mr-2 sm:mr-3 text-emerald-600" />
                {t('products.filterBy')}
              </div>
              <div className="text-xs sm:text-sm font-medium text-neutral-600 bg-neutral-50 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg whitespace-nowrap">
                <span className="text-emerald-600 font-semibold">{filteredAndSortedProducts.length}</span>/{products.length}
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {/* Category Filter */}
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="h-9 sm:h-11 text-xs sm:text-sm">
                  <SelectValue placeholder={t('products.allCategories')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('products.allCategories')}</SelectItem>
                  <SelectItem value="wheelie-bin">{t('products.categories.wheelie-bin')}</SelectItem>
                  <SelectItem value="recycle-bin">{t('products.categories.recycle-bin')}</SelectItem>
                  <SelectItem value="recycle-set">{t('products.categories.recycle-set')}</SelectItem>
                  <SelectItem value="barrel">{t('products.categories.barrel')}</SelectItem>
                </SelectContent>
              </Select>
              {/* Capacity Filter */}
              <Select value={capacityFilter} onValueChange={setCapacityFilter}>
                <SelectTrigger className="h-9 sm:h-11 text-xs sm:text-sm">
                  <SelectValue placeholder={t('products.allCapacities')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('products.allCapacities')}</SelectItem>
                  {uniqueCapacities.map(cap => (
                    <SelectItem key={cap} value={cap}>{cap}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {/* Type Filter */}
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="h-9 sm:h-11 text-xs sm:text-sm">
                  <SelectValue placeholder={t('products.allTypes')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('products.allTypes')}</SelectItem>
                  <SelectItem value="residential">{t('products.residential')}</SelectItem>
                  <SelectItem value="municipal">{t('products.municipal')}</SelectItem>
                  <SelectItem value="industrial">{t('products.industrial')}</SelectItem>
                </SelectContent>
              </Select>
              {/* Sort by Size */}
              <Select value={sortOrder} onValueChange={setSortOrder}>
                <SelectTrigger className="h-9 sm:h-11 text-xs sm:text-sm">
                  <ArrowUpDown size={14} className="mr-1 sm:mr-2" />
                  <SelectValue placeholder={t('products.sortBySize')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">{t('products.sortDefault')}</SelectItem>
                  <SelectItem value="size-asc">{t('products.sortSizeAsc')}</SelectItem>
                  <SelectItem value="size-desc">{t('products.sortSizeDesc')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-8 sm:py-12 lg:py-20 bg-gradient-to-b from-white to-neutral-50 flex-1">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          {filteredAndSortedProducts.length === 0 ? (
            <div className="text-center py-12 sm:py-20">
              <p className="text-base sm:text-xl text-neutral-600 mb-4 sm:mb-6">{t('products.noProducts')}</p>
              <Button 
                onClick={() => {
                  setCapacityFilter('all');
                  setTypeFilter('all');
                  setCategoryFilter('all');
                  setSortOrder('default');
                }}
                variant="outline"
                className="border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 text-sm sm:text-base"
              >
                {t('products.clearFilters')}
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
              {filteredAndSortedProducts.map((product) => (
                <Card key={product.id} className="overflow-hidden hover:shadow-2xl transition-all duration-300 border-0 shadow-lg card-hover group">
                  <div className="aspect-square bg-neutral-50 overflow-hidden relative">
                    <img 
                      src={product.image} 
                      alt={product.name}
                      className="w-full h-full object-contain group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute top-3 sm:top-4 right-3 sm:right-4">
                      <span className="inline-block text-xs sm:text-sm font-bold text-white bg-emerald-600 px-2.5 sm:px-4 py-1 sm:py-2 rounded-full shadow-lg">
                        {product.capacity}
                      </span>
                    </div>
                    {product.setSize && (
                      <div className="absolute top-3 sm:top-4 left-3 sm:left-4">
                        <span className="inline-block text-[10px] sm:text-xs font-bold text-white bg-teal-600 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full shadow-lg">
                          {product.setSize} {t('products.binSet')}
                        </span>
                      </div>
                    )}
                  </div>
                  <CardContent className="p-4 sm:p-6 lg:p-8">
                    <div className="mb-3 sm:mb-4">
                      <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-2 text-neutral-900 line-clamp-1">{product.name}</h3>
                      <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-2">
                        <span className="inline-block text-[10px] sm:text-xs font-medium text-emerald-700 bg-emerald-50 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full uppercase tracking-wide">
                          {getTypeTranslation(product.type)}
                        </span>
                        <span className="inline-block text-[10px] sm:text-xs font-medium text-teal-700 bg-teal-50 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full uppercase tracking-wide">
                          {getCategoryTranslation(product.category)}
                        </span>
                      </div>
                    </div>
                    <p className="text-neutral-600 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed line-clamp-2">{product.shortDesc}</p>
                    
                    {/* Material Options */}
                    {product.materials && (
                      <div className="mb-3 sm:mb-4">
                        <p className="text-[10px] sm:text-xs font-semibold text-neutral-700 mb-1.5 sm:mb-2">{t('products.materialOptions')}:</p>
                        <div className="flex flex-wrap gap-1.5 sm:gap-2">
                          {product.materials.includes('original') && (
                            <Badge variant="outline" className="text-[10px] sm:text-xs border-blue-300 text-blue-700 bg-blue-50 px-1.5 sm:px-2">
                              <Package size={10} className="mr-0.5 sm:mr-1" />
                              {t('products.materials.original')}
                            </Badge>
                          )}
                          {product.materials.includes('recycled') && (
                            <Badge variant="outline" className="text-[10px] sm:text-xs border-green-300 text-green-700 bg-green-50 px-1.5 sm:px-2">
                              <Leaf size={10} className="mr-0.5 sm:mr-1" />
                              {t('products.materials.recycled')}
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}
                    
                    <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6 p-3 sm:p-4 bg-neutral-50 rounded-lg sm:rounded-xl">
                      <div className="text-[10px] sm:text-xs text-neutral-600">
                        <span className="font-semibold text-neutral-800">{t('products.material')}:</span> <span className="line-clamp-1">{product.material}</span>
                      </div>
                      <div className="text-[10px] sm:text-xs text-neutral-600">
                        <span className="font-semibold text-neutral-800">{t('products.load')}:</span> {product.loadCapacity}
                      </div>
                    </div>
                    <Button asChild className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shadow-lg shadow-emerald-500/20 text-sm sm:text-base">
                      <Link to={`/products/${product.id}`}>{t('products.viewDetails')}</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-600 text-white py-10 sm:py-14 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-3 sm:mb-4">{t('products.customSolution')}</h2>
          <p className="text-emerald-50 text-sm sm:text-base lg:text-lg mb-4 sm:mb-6 lg:mb-8 px-4 sm:px-0">{t('products.customDescription')}</p>
          <Button asChild size="lg" variant="outline" className="bg-white text-emerald-600 hover:bg-neutral-50 border-0 shadow-2xl px-6 sm:px-10 text-sm sm:text-base">
            <Link to="/contact">{t('products.contactUs')}</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Products;
